import clsx from "clsx";
import type { Ref } from "react";
import React, { forwardRef, useRef, useState } from "react";
import { mergeRefs } from "react-merge-refs";
import { useTranslation } from "react-i18next";
import type { ParseKeys } from "i18next";

import type { IFileInputProps } from "./types";
import {
  fileListIterator,
  getFileTypeFormat,
  getFileTypeName,
  validateFileList,
} from "./helper";
import classes from "./styles.module.scss";

import { Box } from "@/components/atoms/box";
import { FileUploadImage } from "@/components/icons";
import { Typography } from "@/components/atoms/typography";
import { useReadOnly } from "@/hooks";
import { notify } from "@/components/atoms/notify";
import { Image } from "@/components/molecules/image";

export const FileInput = forwardRef(function FileInput(
  props: IFileInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const {
    maxSize = { size: 50, unit: "MB" },
    onChangeValue,
    type,
    label,
    sampleLink,
    readOnly: propsReadOnly,
    size,
    maxFilesLength = Infinity,
    concatFilesToOldValue = false,
    value,
    defaultImage,
    ...rest
  } = props;

  const readOnly = useReadOnly(propsReadOnly);
  const { t } = useTranslation("form");
  const [inputValue] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (readOnly) return null;

  function setTranslatedError(errorKey: ParseKeys<"form">) {
    notify.error({
      title: t("errors.default"),
      text: t(errorKey, { count: maxFilesLength }),
    });
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const files = event.dataTransfer.files;

    changeHandler(files);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;

    changeHandler(files);
  };

  const handleBoxClick = () => {
    if (!readOnly && !rest.disabled) inputRef.current?.click();
  };

  function changeHandler(files: FileList | null): void {
    const fileList = fileListIterator(files, value);

    if (
      !readOnly &&
      !rest.disabled &&
      validateFileList(
        fileList,
        type,
        maxSize,
        maxFilesLength,
        setTranslatedError,
      )
    ) {
      if (concatFilesToOldValue) onChangeValue?.(fileList);
      else {
        const fileList = fileListIterator(files, []);

        onChangeValue?.(fileList);
      }
    }
  }

  return (
    <>
      <div className={classes.titleContainer}>
        <Typography fontWeight={"semi-bold"}>{t(label)}</Typography>
        <Typography color={"secondary-text"}>
          {type === "excel" && t("excelFileDesc")}
          {type === "image" && t("imageFileDesc")}
          {type === "multiType" && t("multipleFileDesc")}
        </Typography>
      </div>
      <Box
        px={"l4"}
        py={"l4"}
        radius={"md"}
        borderStyle={"dashed"}
        backgroundColor={"secondary-background"}
        borderColor={dragging ? "primary-main" : "border-secondary"}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBoxClick}
        className={clsx([classes.box])}
      >
        <div className={"d-inline-flex justify-content-center"}>
          {defaultImage ? (
            <div
              className={"d-inline-flex"}
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Image containerClassName={classes.image} src={defaultImage} />
            </div>
          ) : (
            <FileUploadImage />
          )}
        </div>
        <div className={"d-flex-column justify-content-center gap-xs"}>
          <Typography fontWeight={"semi-bold"} center>
            <span>{`${t(getFileTypeName(type))} ${t(
              "fileInput.dragHere",
            )}`}</span>
          </Typography>
          <Typography color={"secondary-text"} center>{`${t(
            "fileInput.review",
          )} (${maxSize.size} ${maxSize.unit})`}</Typography>
        </div>
        <input
          type={"file"}
          ref={mergeRefs([inputRef, ref])}
          style={{ opacity: "0", width: "0", height: "0", overflow: "hidden" }}
          accept={getFileTypeFormat(type).join(", ")}
          multiple
          value={inputValue}
          onChange={handleInputChange}
          readOnly={readOnly}
          {...rest}
        />
      </Box>
    </>
  );
});
